import { assert } from "../project";
import type {
  FieldConfig,
  HelpTextConfig,
  InputConfig,
  SelectConfig,
} from "./config/config";

export enum ValidationState {
  VALID = 0,
  INVALID = 1,
}

export class Form {
  public root: HTMLElement;
  private navigation: Element;

  private body: Element;
  private activePage: number;
  private pages: (Page | FinishPage)[];
  private pageLinks: PageLink[];
  private finishPage: FinishPage | undefined;

  constructor(onSubmit: () => void) {
    this.root = document.createElement("form");
    this.root.setAttribute("action", "/");
    this.root.setAttribute("method", "POST");
    this.root.classList.add(
      "flex-grow-1",
      "d-flex",
      "flex-column",
      "gap-3",
      "mh-100",
    );
    this.root.innerHTML = `
        <div class="row flex-grow-1 pb-5">
            <div class="col col-12 col-lg-4 col-xl-3 flex-grow-1">
                <div id="naviation" class="list-group"></div>
            </div>
            <div class="col col-12 col-lg-8 col-xl-9 flex-grow-1">
                <div id="body" class="card mh-100">
                </div>
            </div>
        </div>
    `;

    this.root.addEventListener("submit", (event) => {
      event.preventDefault();
      onSubmit();
    });

    this.navigation = this.root.querySelector("#naviation")!;
    this.body = this.root.querySelector("#body")!;

    this.pages = [];
    this.pageLinks = [];
    this.activePage = 0;
    this.finishPage = undefined;
  }

  public createPage(title: string): Page {
    const index = this.pages.length;

    const pageLink = new PageLink(
      title,
      index,
      () => this.goToPage(index),
      false,
    );
    const page = new Page(
      index + 1,
      title,
      (state) => pageLink.setState(state),
      () => this.goToPage(index + 1),
    );

    this.addPage(page, pageLink);

    return page;
  }

  public finish() {
    const index = this.pages.length;

    this.finishPage = new FinishPage(index);
    const pageLink = new PageLink(
      this.finishPage.title,
      index,
      () => this.goToPage(index),
      true,
    );

    this.addPage(this.finishPage, pageLink);

    this.activePage = 0;
    this.pageLinks[this.activePage].activate();
    this.pages[this.activePage].show();
  }

  public update(state: ValidationState, content: any) {
    assert(this.finishPage !== undefined, "Finish page is not defined");
    this.finishPage.setState(state);

    const finishPageLink = this.pageLinks[this.pageLinks.length - 1];
    finishPageLink.setState(state);

    this.finishPage.setContent(content);
  }

  private addPage(page: Page | FinishPage, link: PageLink) {
    this.pages.push(page);
    this.pageLinks.push(link);

    this.body.appendChild(page.root);
    this.navigation.appendChild(link.root);
  }

  private goToPage(index: number) {
    this.pageLinks[this.activePage].deactivate();

    // The validation results should be rendered when the user navigates to the page the next time.
    // This also will activate the result indicator in the page link.
    const activePage = this.pages[this.activePage];
    activePage.hide();
    activePage.showValidationResults();

    this.activePage = index;

    this.pageLinks[this.activePage].activate();
    this.pages[this.activePage].show();
  }
}

class PageLink {
  root: HTMLElement;

  private icon: Element;

  constructor(
    title: string,
    index: number,
    onClick: () => void,
    private isFinalLink: boolean,
  ) {
    this.root = document.createElement("a");
    this.root.classList.add("list-group-item", "list-group-item-action");
    this.root.setAttribute("href", "#");
    this.root.innerHTML = `
            <div class="d-flex w-100">
                <div class="align-self-center me-3" style="font-size: 1.5rem;"><i class="bi-question-circle"></i></div>
                <div>
                    <p class="mb-0"><small>Schritt ${index + 1}</small></p>
                    <h6 class="text-break">${title}</h6>
                </div>
            </div>
        `;

    this.icon = this.root.children[0].children[0];

    this.root.addEventListener("click", (event) => {
      event.preventDefault();
      onClick();
    });
  }

  public activate() {
    this.root.classList.add("bg-body-secondary");
  }

  public deactivate() {
    this.root.classList.remove("bg-body-secondary");
  }

  public setState(state: ValidationState) {
    switch (state) {
      case ValidationState.VALID:
        {
          this.icon.innerHTML = `<i class="${this.getSuccessLogo()}"></i>`;
        }
        break;
      case ValidationState.INVALID:
        {
          this.icon.innerHTML = `<i class="${this.getErrorLogo()}"></i>`;
        }
        break;
    }
  }

  private getSuccessLogo(): string {
    if (this.isFinalLink) {
      return "bi-check2-all text-success";
    }
    return "bi-check-circle text-success";
  }

  private getErrorLogo(): string {
    if (this.isFinalLink) {
      return "bi-dash-circle text-warning";
    }
    return "bi-exclamation-circle text-danger";
  }
}

export class Page {
  root: HTMLElement;

  private body: Element;
  private shouldShowValidationResults = false;

  private state = ValidationState.INVALID;

  constructor(
    step: number,
    public title: string,
    private onStateChange: (state: ValidationState) => void,
    goToNextPage: () => void,
  ) {
    this.root = document.createElement("div");
    this.root.classList.add("d-none", "flex-column", "h-100", "mh-100");
    this.root.setAttribute("hidden", "true");

    this.root.innerHTML = `
        <div id="header" class="card-body">     
          <h2 class="fs-6 text-muted"></h2>
          <h3 class="mb-0"></h3>
        </div>
        <div id="body" class="flex-grow-1 overflow-y-scroll card-body border-top border-bottom">
        </div>
        <div class="card-body">
            <button type="button" class="btn btn-primary">Nächste Seite</button>
        </div>
    `;

    const stepHeader = this.root.querySelector("h2")!;
    stepHeader.innerText = `Schritt ${step}`;
    const titleHeader = this.root.querySelector("h3")!;
    titleHeader.innerText = this.title;

    this.body = this.root.querySelector("#body")!;

    const button = this.root.querySelector("button")!;
    button.addEventListener("click", () => {
      this.showValidationResults();

      if (this.state === ValidationState.VALID) {
        goToNextPage();
      }
    });
  }

  public showValidationResults() {
    this.shouldShowValidationResults = true;
    this.root.classList.add("was-validated");
    this.onStateChange(this.state);
  }

  public createSubSection(title: string): Section {
    const section = new Section(title);
    this.body.appendChild(section.root);

    return section;
  }

  public createInput(
    config: FieldConfig,
    onUpdate: (state: ValidationState) => void,
  ): Input {
    const input = createInput(config, onUpdate);
    this.body.appendChild(input.root);

    return input;
  }

  public createHelpText(config: HelpTextConfig): HelpText {
    const helpText = new HelpText(config);
    this.body.appendChild(helpText.root);

    return helpText;
  }

  public setErrorMessage(_message: string | undefined) {
    console.warn("setErrorMessage is not implemented for Page");
  }

  public setEnabled(_isEnabled: boolean) {
    console.warn("setEnabled is not implemented for Page");
  }

  public hide() {
    this.root.setAttribute("hidden", "true");
    this.root.classList.add("d-none");
    this.root.classList.remove("d-flex");
  }

  public show() {
    this.root.removeAttribute("hidden");
    this.root.classList.remove("d-none");
    this.root.classList.add("d-flex");
  }

  public setState(state: ValidationState) {
    this.state = state;

    if (this.shouldShowValidationResults) {
      this.onStateChange(state);
    }
  }
}

export class FinishPage {
  public title = "Zusammenfassung";

  public root: HTMLElement;

  private jsonContent: string;
  private submitButton: Element;

  constructor(step: number) {
    this.root = document.createElement("div");
    this.root.setAttribute("hidden", "true");

    this.root.innerHTML = `
        <div id="header" class="card-body">     
          <h2 class="fs-6 text-muted"></h2>
          <h3 class="mb-0"></h3>
        </div>
        <div id="body" class="card-body border-top border-bottom">
          <h4>JSON Nachricht</h4>
          <div class="card text-bg-light">
            <div class="card-body d-flex flex-row">
              <pre class="m-0 flex-fill"></pre>
              <div>
                <button id="copy-url" type="button" class="btn btn-light"><i class="bi bi-clipboard"></i></button>
              </div>
            </div>
          </div>
        </div>
        <div class="card-body">
            <button type="submit" class="btn btn-primary">Antrag Abschicken</button>
        </div>
        `;

    const stepHeader = this.root.querySelector("h2")!;
    stepHeader.innerText = `Schritt ${step}`;
    const titleHeader = this.root.querySelector("h3")!;
    titleHeader.innerText = this.title;

    this.submitButton = this.root.querySelector("button[type=submit]")!;

    this.jsonContent = "";
    const copyButton = this.root.querySelector("#copy-url")!;
    copyButton.addEventListener("click", () => {
      navigator.clipboard.writeText(this.jsonContent);
    });
  }

  // Just an empty implementation to have the same interface as `Page`.
  // TODO: Maybe find a better solution than to force the same interface for both pages.
  public showValidationResults() {}

  public hide() {
    this.root.setAttribute("hidden", "true");
  }

  public show() {
    this.root.removeAttribute("hidden");
  }

  public setState(state: ValidationState) {
    switch (state) {
      case ValidationState.VALID:
        {
          this.submitButton.removeAttribute("disabled");
        }
        break;
      case ValidationState.INVALID:
        {
          this.submitButton.setAttribute("disabled", "true");
        }
        break;
    }
  }

  public setContent(content: any) {
    this.jsonContent = JSON.stringify(content, null, 2);

    const codeContainer = this.root.querySelector("pre")!;
    codeContainer.innerText = this.jsonContent;
  }
}

export class Section {
  root: HTMLElement;
  errorBox: Element;

  level: number;

  constructor(title: string, level = 0) {
    this.root = document.createElement("div");
    this.level = level;

    let heading = "h6";
    if (this.level === 0) {
      heading = "h2";
    } else if (this.level === 1) {
      heading = "h3";
    } else if (this.level === 2) {
      heading = "h4";
    } else if (this.level === 3) {
      heading = "h5";
    }

    this.root.classList.add("mb-5");
    this.root.innerHTML = `
            <${heading}>${title}</${heading}>
            <div class="section-error"></div>
        `;

    this.errorBox = this.root.children[1];
  }

  public setEnabled(isEnabled: boolean) {
    if (isEnabled) {
      this.root.removeAttribute("hidden");
    } else {
      this.root.setAttribute("hidden", "true");
    }
  }

  public createSubSection(title: string): Section {
    const section = new Section(title, this.level + 1);
    this.root.appendChild(section.root);

    return section;
  }

  public createHelpText(config: HelpTextConfig): HelpText {
    const helpText = new HelpText(config);
    this.root.appendChild(helpText.root);

    return helpText;
  }

  public createInput(
    config: FieldConfig,
    onUpdate: (state: ValidationState) => void,
  ): Input {
    const input = createInput(config, onUpdate);
    this.root.appendChild(input.root);

    return input;
  }

  public setErrorMessage(message: string | undefined) {
    if (message === undefined) {
      this.errorBox.innerHTML = "";
    } else {
      this.errorBox.innerHTML = `
                <div class="alert alert-danger" role="alert">
                    ${message}
                </div>
            `;
    }
  }
}

export class HelpText {
  root: HTMLElement;

  constructor(config: HelpTextConfig) {
    this.root = document.createElement("div");
    this.root.classList.add("alert", "alert-info", "mb-3");
    this.root.innerText = config.value;
  }

  public setEnabled(isEnabled: boolean) {
    if (isEnabled) {
      this.root.removeAttribute("hidden");
    } else {
      this.root.setAttribute("hidden", "true");
    }
  }
}

export type Input = TextInput | SelectInput;

export function createInput(
  config: FieldConfig,
  onUpdate: (state: ValidationState) => void,
): Input {
  if (config.type === "input") {
    return new TextInput(config, onUpdate);
  } else {
    console.assert(config.type === "select");
    return new SelectInput(config, onUpdate);
  }
}

export class TextInput {
  root: HTMLElement;
  inputElement: HTMLInputElement;

  constructor(config: InputConfig, onUpdate: (state: ValidationState) => void) {
    this.root = document.createElement("div");
    this.root.classList.add("mb-3");
    this.root.innerHTML = `
            <label for="${config.id}" class="form-label">${config.label}</label>
            <input 
                id="${config.id}" 
                type="text" 
                class="form-control" 
            />
        `;

    this.inputElement = this.root.children[1] as HTMLInputElement;

    if (config.required) {
      this.inputElement.setAttribute("required", "true");
    }

    if (config.minLength) {
      this.inputElement.setAttribute("minLength", config.minLength.toString());
    }

    if (config.maxLength) {
      this.inputElement.setAttribute("maxLength", config.maxLength.toString());
    }

    if (config.pattern) {
      this.inputElement.setAttribute("pattern", config.pattern.toString());
    }

    this.inputElement.addEventListener("input", (_event) => {
      onUpdate(this.validate());
    });
  }

  public setEnabled(isEnabled: boolean) {
    if (isEnabled) {
      this.root.removeAttribute("hidden");
    } else {
      this.root.setAttribute("hidden", "true");
    }
  }

  public validate(): ValidationState {
    if (this.inputElement.validity.valid) {
      return ValidationState.VALID;
    }

    return ValidationState.INVALID;
  }

  public getValue(): string | undefined {
    // TODO(Felix): Maybe trim whitespace at the edges first?
    const value = this.inputElement.value;
    if (value === "") {
      return undefined;
    } else {
      return value;
    }
  }
}

export class SelectInput {
  root: HTMLElement;
  inputElement: HTMLSelectElement;

  constructor(
    config: SelectConfig,
    onUpdate: (state: ValidationState) => void,
  ) {
    this.root = document.createElement("div");
    this.root.classList.add("mb-3");
    this.root.innerHTML = `
            <label for="${config.id}" class="form-label">${config.label}</label>
            <select 
                id="${config.id}" 
                type="text" 
                class="form-select" 
            >
            <option disabled selected value> -- Keine Angabe -- </option>
            </select>
        `;

    this.inputElement = this.root.children[1] as HTMLSelectElement;

    for (const [value, label] of config.options) {
      const option = document.createElement("option");
      option.setAttribute("value", value);
      option.innerText = label;
      this.inputElement.add(option);
    }

    if (config.required) {
      this.inputElement.setAttribute("required", "true");
    }

    this.inputElement.addEventListener("input", (_event) => {
      onUpdate(this.validate());
    });
  }

  public setEnabled(isEnabled: boolean) {
    if (isEnabled) {
      this.root.removeAttribute("hidden");
    } else {
      this.root.setAttribute("hidden", "true");
    }
  }

  public validate(): ValidationState {
    if (this.inputElement.validity.valid) {
      return ValidationState.VALID;
    }
    return ValidationState.INVALID;
  }

  public getValue(): string | undefined {
    const value = this.inputElement.value;
    if (value === "") {
      return undefined;
    } else {
      return value;
    }
  }
}
